import { decoratorsJson } from "@/components/suggestions/providers/data";
import { prioritizeSuggestionRules } from "@/input/utils/editOperators";
import { getClosingSymbolProps, getOpeningSymbolProps } from "@/models/nodes/groups/utils";

import { appendLatexKeywords } from "./utils/appendLatexKeywords";
import { checkFilterRules } from "./utils/filterRules";
import { filterSuggestions } from "./utils/filterSuggestions";
import { normalizeSuggestionKeywords } from "./utils/normalizeKeywords";

import type { DecoratorTypeValue } from "@/components/suggestions/providers/data/types";
import type {
  NodelessSuggestionType,
  SingleSuggestion,
  SuggestionNodeType,
  SuggestionProps,
  SuggestionProvider,
} from "@/components/suggestions/types";
import type { DocumentModel } from "@/models/DocumentModel";
import type { NodeModel } from "@/models/nodes/types";
import type { KeywordLang } from "@corca/data";

const decoratorSuggestionMap: Record<DecoratorTypeValue, SingleSuggestion> = Object.entries(
  decoratorsJson,
).reduce(
  (acc, [decoratorType, decorator]) => {
    const { substitution, ...decoratorData } = decorator;

    acc[decoratorType as DecoratorTypeValue] = {
      type: "single",
      icon: decoratorData.icon,
      forceIcon: decoratorData.forceIcon,
      item: {
        type: "variable",
        intlName: decoratorData.intlName,
        symbol: decoratorType,
        keywords: appendLatexKeywords(
          decoratorData.key,
          normalizeSuggestionKeywords(decoratorData),
        ),
        props: undefined,
      },
      priority: decoratorData.priority ?? 1,
      substitution: {
        cursorPlacement: "after" as const,
        applyTo: "nodeBefore" as const,
        ...substitution,
      },
      priorityRules: decoratorData.priorityRules,
      filterRules: decoratorData.filterRules,
    };

    return acc;
  },
  {} as Record<DecoratorTypeValue, SingleSuggestion>,
);

function mapNodeToSuggestionProps(
  node: NodeModel<Exclude<SuggestionNodeType, NodelessSuggestionType>>,
): SuggestionProps[SuggestionNodeType] | undefined {
  switch (node.type) {
    case "group": {
      return {
        openingSymbol: getOpeningSymbolProps(node.openingSymbol),
        closingSymbol: getClosingSymbolProps(node.closingSymbol),
      };
    }

    default: {
      return undefined;
    }
  }
}

function mapDecoratorToSuggestion(
  node: NodeModel<Exclude<SuggestionNodeType, NodelessSuggestionType>>,
  decoratorType: DecoratorTypeValue,
): SingleSuggestion {
  const decoratorSuggestion = decoratorSuggestionMap[decoratorType];

  return {
    ...decoratorSuggestion,
    item: {
      ...decoratorSuggestion.item,
      node,
      type: node.type,
      props: mapNodeToSuggestionProps(node),
    },
  };
}

function filterDecoratorSuggestions(
  input: string,
  suggestions: SingleSuggestion[],
  doc: DocumentModel,
  lang?: KeywordLang,
): SingleSuggestion[] {
  const filteredSuggestions = filterSuggestions(input, suggestions, { lang });

  return filteredSuggestions.filter((suggestion) => {
    if (suggestion.filterRules) {
      return checkFilterRules(suggestion.filterRules, {
        index: doc.cursor.index,
        node: doc.cursor.node,
      });
    }

    return false;
  });
}

function isValidSuggestionNodeType(
  node?: NodeModel,
): node is NodeModel<Exclude<SuggestionNodeType, NodelessSuggestionType>> {
  switch (node?.type) {
    case "number":
    case "variable":
    case "operator":
    case "operatorSymbol":
    case "group":
    case "constant":
    case "function":
    case "plainText":
    case "symbol":
    case "placeholder":
      return true;
    default:
      return false;
  }
}

const PRIMARY_SELECTION_DECORATOR: Set<DecoratorTypeValue> = new Set(["hat", "overline"]);

function selectionDecoratorSuggestions(doc: DocumentModel): SingleSuggestion[] {
  return Object.entries(decoratorSuggestionMap)
    .filter(
      ([, suggestion]) =>
        suggestion.filterRules &&
        checkFilterRules(suggestion.filterRules, {
          index: doc.cursor.index,
          node: doc.cursor.node,
          selectedNodes: doc.selection.selectedNodes,
          selectedRequired: true,
        }),
    )
    .map(([decoratorType, suggestion]) => ({
      ...suggestion,
      /**
       * Priorities are set here, not in `decorators.json`: `hat` must outrank its siblings to
       * become the submenu's parent, but that parent must not outrank the other `apply` rows.
       */
      priority: PRIMARY_SELECTION_DECORATOR.has(decoratorType) ? 1 : 0.9,
      substitution: {
        cursorPlacement: "after" as const,
        ...suggestion.substitution,
        applyTo: "selectedNode" as const,
      },
      forceIcon: true,
      nestedKey: "decorators",
      nestedOnly: true,
      popupGroup: "apply" as const,
    }));
}

export const decoratorsSuggestionsProvider: SuggestionProvider = {
  type: "decorator",
  search: (input, doc, lang) => {
    if (doc.selection.isMathOnlySelection) {
      return [selectionDecoratorSuggestions(doc)];
    }

    const { nodeBeforeCursor: node } = doc.cursor;

    /**
     * TODO: proper typing for node that is being passed to `mapDecoratorToSuggestion`
     */
    if (!isValidSuggestionNodeType(node)) {
      return [];
    }

    const suggestions: SingleSuggestion[] = Object.keys(decoratorsJson).map((decoratorType) => {
      return mapDecoratorToSuggestion(node, decoratorType as DecoratorTypeValue);
    });
    const filteredSuggestions = filterDecoratorSuggestions(input, suggestions, doc, lang);

    const result = filteredSuggestions.map((suggestion) => {
      const priority = prioritizeSuggestionRules(suggestion, doc.cursor);
      return { ...suggestion, priority };
    });

    return [result];
  },
};
